import { COLOURS, DIFFICULTIES, difficultyForWpm, difficultyLabel, MAX_PLAYERS, NAME_MAX, PACKS, TEAM_COLOURS, TEAM_NAMES, VEHICLES, type PlayerInfo, type RoomInfo, type RoomOptions } from '@typing-racer/shared';
import { clear, h, VEHICLE_EMOJI } from '../dom.js';
import type { StyleId } from '../game/style.js';
import { affiliatePanel } from './affiliate.js';
import { VIEW_MODES, type ViewMode } from '../game/scene.js';
import { MUSIC_TRACKS, TYPING_SOUNDS, type MusicTrack, type TypingSound } from '../audio.js';

export interface LobbyModel {
  joined: boolean;
  online: boolean;
  status: string;
  myId: string | null;
  name: string;
  vehicle: string;
  colour: string;
  style: StyleId;
  muted: boolean;
  musicOn: boolean;
  engineOn: boolean;
  quizOn: boolean;
  music: MusicTrack;
  typing: TypingSound;
  view: ViewMode;
  players: PlayerInfo[];
  options: RoomOptions;
  /** The room we are in (once joined). */
  room: RoomInfo | null;
  /** Room code from an invite link, shown before joining. */
  invite: string | null;
  /** Signed-in account name, if any. */
  account: string | null;
  /** Which auth form is open. */
  authMode: 'none' | 'login' | 'register';
  authBusy: boolean;
}

export interface LobbyActions {
  setName(n: string): void;
  setVehicle(v: string): void;
  setColour(c: string): void;
  setStyle(s: StyleId): void;
  setMuted(m: boolean): void;
  setMusic(t: MusicTrack): void;
  setMusicOn(on: boolean): void;
  setEngineOn(on: boolean): void;
  setQuizOn(on: boolean): void;
  setTypingSound(t: TypingSound): void;
  setView(v: ViewMode): void;
  lessons(): void;
  languages(): void;
  subjects(): void;
  joinOnline(): void;
  createRoom(): void;
  joinRoom(code: string): void;
  copyLink(): void;
  setTeam(team: number): void;
  practice(): void;
  leave(): void;
  ready(r: boolean): void;
  start(): void;
  setOptions(o: Partial<RoomOptions>): void;
  showLeague(): void;
  showAbout(): void;
  showStats(): void;
  setAuthMode(m: 'none' | 'login' | 'register'): void;
  submitAuth(name: string, password: string): void;
  signOut(): void;
}

export function renderLobby(root: HTMLElement, m: LobbyModel, a: LobbyActions): void {
  clear(root);
  const me = m.players.find((p) => p.id === m.myId);
  const isHost = !!me?.isHost;

  const picker = h(
    'div',
    { class: 'stack' },
    h('label', {}, 'Vehicle'),
    h(
      'div',
      { class: 'vehicles' },
      VEHICLES.map((v) =>
        h(
          'button',
          {
            class: `vehicle-btn${v.id === m.vehicle ? ' selected' : ''}`,
            title: v.blurb,
            'aria-pressed': v.id === m.vehicle,
            onclick: () => a.setVehicle(v.id),
          },
          h('span', { class: 'emoji' }, VEHICLE_EMOJI[v.id] ?? '🚗'),
          v.name,
        ),
      ),
    ),
    h('label', {}, 'Colour'),
    h(
      'div',
      { class: 'swatches', role: 'radiogroup' },
      COLOURS.map((c) =>
        h('button', {
          class: `swatch${c.hex === m.colour ? ' selected' : ''}`,
          style: `background:${c.hex}`,
          title: c.name,
          'aria-label': c.name,
          onclick: () => a.setColour(c.hex),
        }),
      ),
    ),
    h(
      'div',
      { class: 'row' },
      h('label', { style: 'margin:0' }, 'Style'),
      h(
        'div',
        { class: 'toggle' },
        h('button', { class: m.style === 'blocky' ? 'on' : '', onclick: () => a.setStyle('blocky') }, 'Blocky'),
        h('button', { class: m.style === 'model' ? 'on' : '', onclick: () => a.setStyle('model') }, 'Model'),
      ),
      h('label', { style: 'margin:0 0 0 12px' }, 'Camera'),
      h(
        'div',
        { class: 'toggle' },
        VIEW_MODES.map((v) => h('button', { class: m.view === v.id ? 'on' : '', onclick: () => a.setView(v.id) }, v.name)),
      ),
    ),
    h(
      'div',
      { class: 'row' },
      h('label', { style: 'margin:0' }, '🎵 Music'),
      h(
        'div',
        { class: 'toggle' },
        h('button', { class: m.musicOn ? 'on' : '', onclick: () => a.setMusicOn(true) }, 'On'),
        h('button', { class: !m.musicOn ? 'on' : '', onclick: () => a.setMusicOn(false) }, 'Off'),
      ),
      h(
        'div',
        { class: `toggle${m.musicOn ? '' : ' dim'}` },
        MUSIC_TRACKS.map((t) => h('button', { class: m.music === t.id ? 'on' : '', title: t.blurb, disabled: !m.musicOn, onclick: () => a.setMusic(t.id) }, t.name)),
      ),
    ),
    h(
      'div',
      { class: 'row' },
      h('label', { style: 'margin:0' }, '🔊 Sound effects'),
      h(
        'div',
        { class: 'toggle' },
        h('button', { class: !m.muted ? 'on' : '', onclick: () => a.setMuted(false) }, 'On'),
        h('button', { class: m.muted ? 'on' : '', onclick: () => a.setMuted(true) }, 'Off'),
      ),
      h('span', { class: 'muted small' }, 'Typos, nitro and fanfares'),
    ),
    h(
      'div',
      { class: 'row' },
      h('label', { style: 'margin:0' }, '🏎️ Engine sound'),
      h(
        'div',
        { class: 'toggle' },
        h('button', { class: m.engineOn ? 'on' : '', onclick: () => a.setEngineOn(true) }, 'On'),
        h('button', { class: !m.engineOn ? 'on' : '', onclick: () => a.setEngineOn(false) }, 'Off'),
      ),
      h('span', { class: 'muted small' }, 'Hum that rises with your speed'),
    ),
    h(
      'div',
      { class: 'row' },
      h('label', { style: 'margin:0' }, '🧠 Recall quiz'),
      h(
        'div',
        { class: 'toggle' },
        h('button', { class: m.quizOn ? 'on' : '', onclick: () => a.setQuizOn(true) }, 'On'),
        h('button', { class: !m.quizOn ? 'on' : '', onclick: () => a.setQuizOn(false) }, 'Off'),
      ),
      h('span', { class: 'muted small' }, 'Three questions after each race on what you typed'),
    ),
    h(
      'div',
      { class: 'row' },
      h('label', { style: 'margin:0' }, '⌨️ Typing sounds'),
      h(
        'div',
        { class: 'toggle' },
        TYPING_SOUNDS.map((t) => h('button', { class: m.typing === t.id ? 'on' : '', title: t.blurb, onclick: () => a.setTypingSound(t.id) }, t.name)),
      ),
    ),
  );

  let right: HTMLElement;
  if (!m.joined) {
    const input = h('input', {
      type: 'text',
      id: 'name',
      maxlength: NAME_MAX,
      placeholder: 'Your name',
      autocomplete: 'off',
      value: m.account ?? m.name,
      disabled: !!m.account,
      oninput: (e) => a.setName((e.target as HTMLInputElement).value),
      onkeydown: (e) => {
        if ((e as KeyboardEvent).key === 'Enter') a.joinOnline();
      },
    }) as HTMLInputElement;
    let account: HTMLElement;
    if (m.account) {
      account = h(
        'div',
        { class: 'account row', style: 'justify-content:space-between' },
        h('span', {}, '👤 Signed in as ', h('b', {}, m.account)),
        h('span', { class: 'row' }, h('button', { class: 'btn small blue', onclick: () => a.showStats() }, '📈 My progress'), h('button', { class: 'btn small ghost', onclick: () => a.signOut() }, 'Sign out')),
      );
    } else if (m.authMode === 'none') {
      account = h(
        'div',
        { class: 'account row', style: 'justify-content:space-between' },
        h('span', { class: 'small' }, '👤 Guest. Sign in to protect your name and track your progress.'),
        h('span', { class: 'row' }, h('button', { class: 'btn small ghost', onclick: () => a.setAuthMode('login') }, 'Sign in'), h('button', { class: 'btn small blue', onclick: () => a.setAuthMode('register') }, 'Create account')),
      );
    } else {
      const user = h('input', { type: 'text', maxlength: NAME_MAX, placeholder: 'Name', autocomplete: 'username', value: m.name }) as HTMLInputElement;
      const pass = h('input', { type: 'password', placeholder: m.authMode === 'register' ? 'Choose a password (6+ characters)' : 'Password', autocomplete: m.authMode === 'register' ? 'new-password' : 'current-password' }) as HTMLInputElement;
      const submit = () => a.submitAuth(user.value, pass.value);
      pass.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') submit();
      });
      account = h(
        'div',
        { class: 'account stack' },
        h('b', {}, m.authMode === 'register' ? 'Create an account' : 'Sign in'),
        h('div', { class: 'muted small' }, m.authMode === 'register' ? 'Your name becomes yours alone, and every race and lesson is tracked so you can see your progress.' : 'Enter the name and password you registered with.'),
        user,
        pass,
        h(
          'div',
          { class: 'row' },
          h('button', { class: 'btn small primary', disabled: m.authBusy, onclick: submit }, m.authMode === 'register' ? 'Create account' : 'Sign in'),
          h('button', { class: 'btn small ghost', onclick: () => a.setAuthMode(m.authMode === 'register' ? 'login' : 'register') }, m.authMode === 'register' ? 'I have an account' : 'Create one instead'),
          h('button', { class: 'btn small ghost', onclick: () => a.setAuthMode('none') }, 'Cancel'),
        ),
      );
    }
    right = h(
      'div',
      { class: 'panel stack' },
      h('h2', {}, m.account ? 'Ready to race' : 'Enter your name'),
      input,
      account,
      m.invite
        ? h(
            'div',
            { class: 'invite' },
            h('b', {}, `🔗 You've been invited to room ${m.invite}`),
            h('div', { class: 'row', style: 'margin-top:6px' }, h('button', { class: 'btn primary', onclick: () => a.joinRoom(m.invite!) }, `Join room ${m.invite}`), h('button', { class: 'btn small ghost', onclick: () => a.joinRoom('') }, 'Ignore invite')),
          )
        : null,
      h('div', { class: 'row' },
        h('button', { class: 'btn primary', onclick: () => a.joinOnline() }, '🏁 Join public race'),
        h('button', { class: 'btn ghost', onclick: () => a.practice() }, '🎯 Practice offline'),
      ),
      (() => {
        const codeInput = h('input', { type: 'text', maxlength: 5, placeholder: 'Room code', style: 'width:8.5em;text-transform:uppercase', autocomplete: 'off', onkeydown: (e) => { if ((e as KeyboardEvent).key === 'Enter') a.joinRoom((e.target as HTMLInputElement).value); } }) as HTMLInputElement;
        return h(
          'div',
          { class: 'row' },
          h('button', { class: 'btn blue', onclick: () => a.createRoom() }, '👥 Create private room'),
          codeInput,
          h('button', { class: 'btn small ghost', onclick: () => a.joinRoom(codeInput.value) }, 'Join code'),
        );
      })(),
      h('p', { class: 'muted kbd-note' }, 'Up to 6 players race the same sentences. Empty seats are filled by bots. Best played with a keyboard.'),
      h(
        'div',
        { class: 'row' },
        h('button', { class: 'btn blue', onclick: () => a.lessons() }, '📚 Typing lessons'),
        h('button', { class: 'btn blue', onclick: () => a.languages() }, '🌍 Learn a language'),
        h('button', { class: 'btn blue', onclick: () => a.subjects() }, '🧠 Learn a subject'),
        h('button', { class: 'btn small ghost', onclick: () => a.showLeague() }, '🏆 League table'),
        h('button', { class: 'btn small ghost', onclick: () => a.showAbout() }, 'ℹ️ About'),
      ),
      h('p', { class: 'muted', style: 'font-size:0.8rem' }, m.status),
    );
    if (!m.account && m.authMode === 'none') setTimeout(() => input.focus(), 0);
  } else {
    const teamBadge = (p: PlayerInfo) =>
      p.team === null || m.options.teams === 0
        ? null
        : h('span', { class: 'badge team', style: `background:${TEAM_COLOURS[p.team] ?? '#999'};color:#fff` }, TEAM_NAMES[p.team] ?? `Team ${p.team + 1}`);
    const slots: HTMLElement[] = m.players.map((p) =>
      h(
        'li',
        {},
        h('span', { class: 'dot', style: `background:${p.colour}` }),
        h('span', {}, VEHICLE_EMOJI[p.vehicle] ?? '🚗'),
        h('span', { class: 'name' }, p.name, p.id === m.myId ? ' (you)' : ''),
        teamBadge(p),
        p.isHost ? h('span', { class: 'badge' }, 'Host') : null,
        p.isBot ? h('span', { class: 'badge bot' }, `Bot · ${difficultyForWpm(m.options.botWpm).name} ${m.options.botWpm} WPM`) : null,
        p.ready && !p.isBot ? h('span', { class: 'badge ready' }, 'Ready') : null,
      ),
    );
    for (let i = m.players.length; i < MAX_PLAYERS; i++) {
      slots.push(h('li', { class: 'empty-slot' }, m.options.fillWithBots ? `Bot seat · ${difficultyLabel(m.options.botWpm)}` : 'Empty seat'));
    }
    const opts = h(
      'div',
      { class: 'stack', style: 'margin-top:10px' },
      h('label', {}, 'Race options', isHost ? '' : ' (host only)'),
      h(
        'div',
        { class: 'row' },
        h('span', {}, 'Sentences'),
        h('input', {
          type: 'number',
          min: 1,
          max: 15,
          value: m.options.sentenceCount,
          disabled: !isHost,
          onchange: (e) => a.setOptions({ sentenceCount: Number((e.target as HTMLInputElement).value) }),
        }),
        h(
          'label',
          { style: 'margin:0;display:inline-flex;gap:6px;align-items:center' },
          h('input', {
            type: 'checkbox',
            checked: m.options.fillWithBots,
            disabled: !isHost,
            onchange: (e) => a.setOptions({ fillWithBots: (e.target as HTMLInputElement).checked }),
          }),
          'Fill with bots',
        ),
      ),
    );
    const pack = h(
      'div',
      { class: 'stack', style: 'margin-top:6px' },
      h('label', { style: 'margin:0' }, '📖 Sentence pack'),
      h(
        'div',
        { class: 'packs' },
        PACKS.map((p) =>
          h('button', { class: `pack-btn${p.id === m.options.pack ? ' selected' : ''}`, disabled: !isHost, 'aria-pressed': p.id === m.options.pack, onclick: () => a.setOptions({ pack: p.id }) }, p.name),
        ),
      ),
      h('div', { class: 'muted small' }, m.options.pack === 'motivation' ? 'Motivational sentences.' : 'Race on real facts and learn as you type.'),
    );
    const roomBanner = m.room && !m.room.isPublic
      ? h(
          'div',
          { class: 'invite' },
          h('div', { class: 'row', style: 'justify-content:space-between' },
            h('span', {}, '🔒 Private room ', h('b', { class: 'code' }, m.room.code)),
            h('button', { class: 'btn small blue', onclick: () => a.copyLink() }, '🔗 Copy invite link'),
          ),
          h('div', { class: 'muted small' }, 'Friends can join with the link or by typing the code on the main menu.'),
        )
      : h('div', { class: 'muted small' }, '🌐 Public room: anyone on the site can join. Create a private room to race just your friends.');
    const teamPicker = h(
      'div',
      { class: 'stack', style: 'margin-top:6px' },
      h('label', { style: 'margin:0' }, '👥 Teams', isHost ? '' : ' (host only)'),
      h(
        'div',
        { class: 'row' },
        h(
          'div',
          { class: 'toggle' },
          [0, 2, 3].map((n) => h('button', { class: m.options.teams === n ? 'on' : '', disabled: !isHost, onclick: () => a.setOptions({ teams: n }) }, n === 0 ? 'Solo' : `${n} teams`)),
        ),
        m.options.teams > 0 && me
          ? h(
              'div',
              { class: 'row' },
              h('span', { class: 'small' }, 'Your team:'),
              TEAM_NAMES.slice(0, m.options.teams).map((name, i) =>
                h('button', { class: `team-btn${me.team === i ? ' selected' : ''}`, style: `--team:${TEAM_COLOURS[i]}`, onclick: () => a.setTeam(i) }, name),
              ),
            )
          : null,
      ),
      m.options.teams > 0 ? h('div', { class: 'muted small' }, 'Team score is the sum of placing points; bots count for their team too.') : null,
    );
    const current = difficultyForWpm(m.options.botWpm);
    const difficulty = h(
      'div',
      { class: 'stack', style: 'margin-top:6px' },
      h('label', { style: 'margin:0' }, '🤖 Bot difficulty'),
      h(
        'div',
        { class: 'difficulty' },
        DIFFICULTIES.map((d) =>
          h(
            'button',
            {
              class: `diff-btn${d.wpm === m.options.botWpm ? ' selected' : ''}`,
              title: d.blurb,
              disabled: !isHost,
              'aria-pressed': d.wpm === m.options.botWpm,
              onclick: () => a.setOptions({ botWpm: d.wpm }),
            },
            h('b', {}, d.name),
            h('span', {}, `${d.wpm} WPM`),
          ),
        ),
      ),
      h('div', { class: 'muted small' }, `${difficultyLabel(m.options.botWpm)}: ${current.blurb}. Bots vary about 25% around this speed.`),
    );
    right = h(
      'div',
      { class: 'panel stack' },
      h('h2', {}, m.online ? 'Lobby' : 'Practice'),
      m.online ? roomBanner : null,
      h('ul', { class: 'players' }, slots),
      opts,
      m.online ? teamPicker : null,
      pack,
      difficulty,
      h(
        'div',
        { class: 'row' },
        isHost
          ? h('button', { class: 'btn primary', onclick: () => a.start() }, '🏁 Start race')
          : h('button', { class: `btn ${me?.ready ? 'ghost' : 'primary'}`, onclick: () => a.ready(!me?.ready) }, me?.ready ? 'Not ready' : "I'm ready"),
        h('button', { class: 'btn small ghost', onclick: () => a.showLeague() }, '🏆 League'),
        h('button', { class: 'btn small red', onclick: () => a.leave() }, 'Leave'),
      ),
      isHost ? null : h('p', { class: 'muted kbd-note' }, 'Waiting for the host to start.'),
      h('p', { class: 'muted', style: 'font-size:0.8rem' }, m.status),
    );
  }

  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'lobby' },
        h('div', { class: 'panel' }, h('h1', {}, 'Typing ', h('span', { class: 'accent' }, 'Racer')), h('p', { class: 'muted' }, 'Type fast, type clean, drive faster.'), picker, m.joined ? null : affiliatePanel()),
        right,
      ),
    ),
  );
}
